import { useUpdateBankAccount } from '@api';
import { BankAccountFormSchemaType, useNotification } from '@common';
import { AxiosError } from 'axios';

const TRANS_KEY = 'NOTIFICATIONS.EDIT_BANK_ACCOUNT';

type UseUpdateBankAccountMutationReturnType = {
  submitUpdateBankAccount: (
    data: BankAccountFormSchemaType,
    bankAccountId: string
  ) => Promise<void>;
};

export const useUpdateBankAccountMutation =
  (): UseUpdateBankAccountMutationReturnType => {
    const { showSuccess, showError } = useNotification();
    const { mutateAsync: updateBankAccount } = useUpdateBankAccount();

    const onSuccess = (): void => {
      showSuccess({
        title: `${TRANS_KEY}.SUCCESS.TITLE`,
        message: `${TRANS_KEY}.SUCCESS.MESSAGE`,
      });
    };

    const onError = (error: AxiosError) => {
      showError({
        title: `${TRANS_KEY}.ERROR.TITLE`,
        message: `${TRANS_KEY}.ERROR.MESSAGE`,
        error,
      });
    };

    const submitUpdateBankAccount = async (
      data: BankAccountFormSchemaType,
      bankAccountId: string
    ): Promise<void> => {
      await updateBankAccount(
        {
          bankAccountId,
          data: {
            accountHolderName: data.accountHolderName,
            accountHolderAddress: {
              street: data.accountHolderAddress.street || null,
              addition: data.accountHolderAddress.addition || null,
              zip: data.accountHolderAddress.zip || null,
              city: data.accountHolderAddress.city || null,
              cityEnglish: data.accountHolderAddress.cityEnglish || null,
              country: data.accountHolderAddress.country || null,
              state: data.accountHolderAddress.state || null,
            },
            currency: data.currency,
            bankName: data.bankName,
            bankAddress: {
              street: data.bankAddress.street || null,
              addition: data.bankAddress.addition || null,
              zip: data.bankAddress.zip || null,
              city: data.bankAddress.city || null,
              cityEnglish: data.bankAddress.cityEnglish || null,
              country: data.bankAddress.country || null,
              state: data.bankAddress.state || null,
            },
            accountNumber: data.accountNumber || null,
            bicSwift: data.bicSwift || null,
            iban: data.iban || null,
            routingNumber: data.routingNumber || null,
            notes: data.notes || null,
            creditorNumber: data.creditorNumber || null,
          },
        },
        { onSuccess, onError }
      );
    };

    return { submitUpdateBankAccount };
  };
